/**
 * PRP-MCP-5: the estimate page's "Chat changes" panel.
 *
 * Lists what a chat app changed on this estimate, newest first: when, which
 * chat app, what changed, before and after, and the request it was answering.
 * Each change and each batch (one chat tool call) has Undo; when the server
 * refuses (for example a later change depends on it) its plain reason is shown.
 * Open questions are listed read only here: they are answered under Open items
 * on the Estimate tab, or in the chat. Since PRP-INTAKE-1 this panel is the
 * estimate page's "Build list" tab. Collapsible; starts open while a change is not undone or a
 * question is open. There is no review step (founder decision D2, 2026-10-11).
 */
import { useMemo, useState } from 'react';

import type { McpChange, McpEstimateChanges } from '../../lib/mcpApi';
import { fmtChangeTime, fmtChangeValues } from '../lib/chatChanges';
import { Banner } from './ui/banner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Pill } from './ui/pill';

/** Props for {@link ChatChangesPanel}. */
export interface ChatChangesPanelProps {
  data: McpEstimateChanges;
  /** Resolves to a refusal message, or null when the undo worked. */
  onUndoChange: (_changeId: string) => Promise<string | null>;
  onUndoBatch: (_batchId: string) => Promise<string | null>;
  /** "Only chat changes" filter state for the estimate lines. */
  onlyChat: boolean;
  onOnlyChatChange: (_v: boolean) => void;
}

function ChangeRow({
  change,
  busy,
  onUndo,
}: {
  change: McpChange;
  busy: boolean;
  onUndo: () => void;
}) {
  const showValues = change.before !== null || change.after !== null;
  // The server refuses to undo a create (delete it in the web app), so the row offers no Undo.
  const isCreate = change.kind === 'create_estimate';
  return (
    <li className="py-2.5 flex gap-3 items-start" data-testid={`chat-change-${change.change_id}`}>
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
          <span className="text-[var(--tx-faint)]">{fmtChangeTime(change.created_at)}</span>
          {change.client_name && <span className="text-[var(--tx-lo)]">{change.client_name}</span>}
          {change.room && <span className="text-[var(--tx-lo)]">{change.room}</span>}
          {!isCreate && change.undone && <Pill kind="neutral">undone</Pill>}
        </div>
        <div className="text-[13px] text-[var(--tx-hi)]">{change.summary}</div>
        {showValues && (
          <div className="text-[12px] text-[var(--tx-mid)]">
            {fmtChangeValues(change.before)} <span aria-hidden="true">-&gt;</span>
            <span className="sr-only">changed to</span> {fmtChangeValues(change.after)}
          </div>
        )}
        {change.request_summary && (
          <div className="text-[12px] text-[var(--tx-lo)]">Request: {change.request_summary}</div>
        )}
      </div>
      {!change.undone && !isCreate && (
        <Button
          size="sm"
          disabled={busy}
          onClick={onUndo}
          aria-label={`Undo ${change.summary}`}
        >
          Undo
        </Button>
      )}
    </li>
  );
}

/** The collapsible change log for one estimate. */
export default function ChatChangesPanel({
  data,
  onUndoChange,
  onUndoBatch,
  onlyChat,
  onOnlyChatChange,
}: ChatChangesPanelProps) {
  const [open, setOpen] = useState(
    data.changes.some((c) => !c.undone && c.kind !== 'create_estimate') || data.parked.length > 0,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Batches newest first, each with its own changes (the log is already newest first).
  const batches = useMemo(() => {
    const byBatch = new Map<string, McpChange[]>();
    for (const c of data.changes) {
      const list = byBatch.get(c.batch_id) ?? [];
      list.push(c);
      byBatch.set(c.batch_id, list);
    }
    const order = data.batches.length > 0 ? data.batches.map((b) => b.batch_id) : [...byBatch.keys()];
    const seen = new Set(order);
    for (const id of byBatch.keys()) if (!seen.has(id)) order.push(id);
    return order
      .filter((id) => byBatch.has(id))
      .map((id) => ({
        id,
        meta: data.batches.find((b) => b.batch_id === id),
        changes: byBatch.get(id) ?? [],
      }));
  }, [data]);

  if (data.changes.length === 0 && data.parked.length === 0) return null;

  const run = async (fn: () => Promise<string | null>) => {
    setBusy(true);
    setError(null);
    try {
      const msg = await fn();
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-3.5 p-4" data-testid="chat-changes-panel">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="flex items-center gap-2 text-left"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="text-[13px] font-semibold text-[var(--tx-hi)]">Chat changes</span>
          <span className="text-[12px] text-[var(--tx-lo)]">
            {data.changes.length} {data.changes.length === 1 ? 'change' : 'changes'}
            {data.parked.length > 0 &&
              `, ${data.parked.length} open ${data.parked.length === 1 ? 'question' : 'questions'}`}
          </span>
          <span className="text-[11px] text-[var(--tx-faint)]">{open ? 'Hide' : 'Show'}</span>
        </button>
        <label className="ml-auto flex items-center gap-1.5 text-[12px] text-[var(--tx-mid)] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={onlyChat}
            onChange={(e) => onOnlyChatChange(e.target.checked)}
            className="accent-[var(--accent)]"
            data-testid="only-chat-toggle"
          />
          Only chat changes
        </label>
      </div>

      {open && (
        <div className="mt-3 space-y-3">
          {error && <Banner tone="warn">{error}</Banner>}

          {data.parked.length > 0 && (
            <div data-testid="chat-parked" className="space-y-1.5">
              <div className="text-[12px] font-[550] text-[var(--tx-mid)]">
                Open questions ({data.parked.length})
              </div>
              <ul className="m-0 pl-5 list-disc text-[12.5px] text-[var(--tx-hi)] space-y-1">
                {data.parked.map((q) => (
                  <li key={q.proposal_id}>
                    {q.question}
                    {q.options.length > 0 && (
                      <ul className="m-0 pl-5 list-[circle] text-[12px] text-[var(--tx-mid)]">
                        {q.options.map((o) => (
                          <li key={o.choice}>
                            <span className="font-[550]">{o.choice}</span>: {o.description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
              <p className="m-0 text-[12px] text-[var(--tx-lo)]">Open item, see the top of the Estimate tab.</p>
            </div>
          )}

          {batches.map(({ id, meta, changes }) => {
            const live = changes.some((c) => !c.undone);
            return (
              <div
                key={id}
                className="rounded-[var(--r-md)] border border-[var(--bd-subtle)] px-3 py-2"
                data-testid={`chat-batch-${id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="flex-1 text-[11.5px] text-[var(--tx-lo)]">
                    {fmtChangeTime(meta?.created_at ?? changes[0]?.created_at)} · {changes.length}{' '}
                    {changes.length === 1 ? 'change' : 'changes'} from one chat request
                    {meta?.undone || !live ? ' · undone' : ''}
                  </span>
                  {live && changes.length > 1 && (
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => void run(() => onUndoBatch(id))}
                      aria-label={`Undo all ${changes.length} changes in this batch`}
                    >
                      Undo all
                    </Button>
                  )}
                </div>
                <ul className="m-0 p-0 list-none divide-y divide-[var(--bd-subtle)]">
                  {changes.map((c) => (
                    <ChangeRow
                      key={c.change_id}
                      change={c}
                      busy={busy}
                      onUndo={() => void run(() => onUndoChange(c.change_id))}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
